import React, { useState } from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    HomeOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    BankOutlined,
    ClockCircleOutlined,
    CheckOutlined,
    DeleteOutlined,
    DollarOutlined
} from "@ant-design/icons";
import { Col, Row, Button, Tooltip } from "antd";
import CommonCard from "../../../global/card";
import CommonDialog from "../../../global/dialog";
import { cn } from "../../../../utils/cn";
import { formatDateTime } from "../../../../utils/functions";

const EnhancedLeadsImportedViaFormDetails = ({
    leadDetail,
    handleAcceptReject
}) => {
    const [toggleModal, setToggleModal] = useState({
        show: false,
        type: "",
    });

    if (!leadDetail) return null;

    const onActionConfirm = async (type) => {
        await handleAcceptReject(leadDetail._id, type);
        setToggleModal({ show: false, type: "" });
    };

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Address: <HomeOutlined />,
            "Event Name": <InfoCircleOutlined />,
            "Event Type": <InfoCircleOutlined />,
            "Event Date": <CalendarOutlined />,
            "Event Time": <ClockCircleOutlined />,
            "Guest Count": <TeamOutlined />,
            Company: <BankOutlined />,
            "Imported At": <CalendarOutlined />,
            Budget: <DollarOutlined />,
            "Notes/Inquiry": <InfoCircleOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    return (
        <div className="p-6" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                title={
                    <div className="flex items-center gap-2">
                        <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                        <span style={{ fontSize: "16px", fontWeight: 600 }}>Lead Details</span>
                    </div>
                }
                extra={
                    <div className="flex gap-2">
                        <Tooltip title="Accept">
                            <Button
                                type="primary"
                                className="bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600"
                                icon={<CheckOutlined />}
                                onClick={() => setToggleModal({ show: true, type: "ACCEPT" })}
                            >
                                Accept
                            </Button>
                        </Tooltip>
                        <Tooltip title="Reject">
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => setToggleModal({ show: true, type: "REJECT" })}
                            >
                                Reject
                            </Button>
                        </Tooltip>
                    </div>
                }
            >
                <Row gutter={[24, 0]}>
                    {/* Photo Section (if available) */}
                    {leadDetail.photo && (
                        <Col span={24} className="border-b border-gray-200 py-4 text-center">
                            <img
                                src={leadDetail.photo}
                                alt="Lead"
                                width={100}
                                style={{ borderRadius: "50%", margin: "0 auto" }}
                            />
                        </Col>
                    )}

                    {/* Basic Information Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Basic Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                {
                                    label: "Name",
                                    value: leadDetail.fullName ? leadDetail.fullName : `${leadDetail.firstName} ${leadDetail.lastName}`
                                },
                                { label: "Email", value: leadDetail?.email },
                                { label: "Phone", value: leadDetail?.phone },
                                { label: "Imported At", value: formatDateTime(leadDetail?.dateAsPerUserTimezone) },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Event Information Section */}
                    <Col span={24} className={cn("py-4", !leadDetail?.inquiry && "border-b-0")}>
                        <div className="flex items-center gap-2 mb-4">
                            <CalendarOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Event Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                { label: "Event Name", value: leadDetail?.eventName },
                                { label: "Event Type", value: leadDetail?.eventType },
                                { label: "Event Date", value: leadDetail?.eventDate },
                                { label: "Guest Count", value: leadDetail?.guestCount },
                                { label: "Budget", value: leadDetail?.budget },
                                { label: "Company", value: leadDetail?.company },
                                { label: "Imported At", value: formatDateTime(leadDetail?.createdAt) },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Notes/Inquiry Section */}
                    {leadDetail?.inquiry && (
                        <Col span={24} className="border-t border-gray-200 pt-4">
                            <div className="flex items-center gap-2 mb-3">
                                <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                                <span style={{ fontSize: "15px", fontWeight: 600 }}>Notes/Inquiry</span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap m-0">{leadDetail?.inquiry}</p>
                        </Col>
                    )}
                </Row>
            </CommonCard>

            {toggleModal.show && (
                <CommonDialog
                    title={toggleModal.type === "ACCEPT" ? "Accept Lead" : "Reject Lead"}
                    description={
                        toggleModal.type === "ACCEPT"
                            ? "Are you sure you want to accept this lead?"
                            : "Are you sure you want to reject this lead? This action cannot be undone."
                    }
                    open={toggleModal.show}
                    setOpen={() => setToggleModal({ show: false, type: "" })}
                    onOk={() => onActionConfirm(toggleModal.type)}
                    onCancel={() => setToggleModal({ show: false, type: "" })}
                />
            )}
        </div>
    );
};

export default EnhancedLeadsImportedViaFormDetails;
